import { NavLink, Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { DashboardSiteProvider, useDashboardSite } from '@/pages/dashboard/DashboardSiteContext';
import DashboardAuthViews from '@/pages/dashboard/DashboardAuthViews';
import { DASHBOARD_CONTACT_ADMIN, DASHBOARD_NAV } from '@/pages/dashboard/dashboardShared';
import { DashboardSitePickerCard } from '@/components/dashboard/DashboardPanels';

const NO_SITE_ALLOWED = new Set([
  '/dashboard/content',
  '/dashboard/billing',
  '/dashboard/security',
  '/dashboard/help',
  '/dashboard/contact',
]);

function navLinkClass(isActive: boolean, locked: boolean, variant: 'default' | 'contact' = 'default') {
  if (variant === 'contact') {
    return [
      'flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
      locked ? 'pointer-events-none opacity-40' : '',
      isActive
        ? 'border border-primary bg-primary text-primary-foreground shadow-sm'
        : 'border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15',
    ].join(' ');
  }
  return [
    'whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors',
    locked ? 'pointer-events-none opacity-40' : '',
    isActive
      ? 'bg-muted font-medium text-foreground'
      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
  ].join(' ');
}

function DashboardShell() {
  const location = useLocation();
  const {
    isAuthenticated,
    authReady,
    isPlatformAdmin,
    mustResetPassword,
    selectedSite,
    selectedSiteRole,
    confirmLeaveIfDirty,
    sites,
  } = useDashboardSite();

  if (!authReady) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Checking session…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <DashboardAuthViews />;
  }

  const hasNoSites = sites.length === 0 && !isPlatformAdmin;
  const pathAllowedWithoutSite =
    NO_SITE_ALLOWED.has(location.pathname) || location.pathname.startsWith('/dashboard/security');

  if (hasNoSites && !pathAllowedWithoutSite) {
    return <Navigate to="/dashboard/content" replace />;
  }

  const contactLocked = false;

  return (
    <TooltipProvider delayDuration={120}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="pl-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {selectedSite?.name || (hasNoSites ? 'Get started' : 'Your Booking Dashboard')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {hasNoSites
                ? 'Add funds, then claim and crawl your website to continue setup.'
                : selectedSiteRole
                  ? `Role: ${selectedSiteRole}`
                  : 'Choose a site to configure'}
            </p>
            {mustResetPassword ? (
              <p className="text-sm text-amber-500">
                Password reset is recommended.{' '}
                <Link to="/dashboard/security" className="underline underline-offset-2">
                  Change password
                </Link>
              </p>
            ) : null}
          </div>
          {isPlatformAdmin ? (
            <Button asChild variant="outline">
              <Link to="/dashboard/admin">Open admin panel</Link>
            </Button>
          ) : null}
        </div>

        {sites.length > 0 ? <DashboardSitePickerCard /> : null}

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <aside className="flex shrink-0 flex-col gap-2 lg:w-44 lg:sticky lg:top-4 lg:min-h-[min(32rem,calc(100vh-10rem))]">
            <nav
              aria-label="Dashboard sections"
              className="flex gap-1 overflow-x-auto border-b border-border pb-2 lg:flex-1 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r lg:pb-0 lg:pr-4"
            >
              {DASHBOARD_NAV.map((item) => {
                if ('adminOnly' in item && item.adminOnly && !isPlatformAdmin) return null;
                const lockedWithoutSite =
                  hasNoSites &&
                  item.to !== '/dashboard/content' &&
                  item.to !== '/dashboard/billing' &&
                  item.to !== '/dashboard/help';
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={(e) => {
                      if (lockedWithoutSite) {
                        e.preventDefault();
                        return;
                      }
                      if (!confirmLeaveIfDirty()) e.preventDefault();
                    }}
                    className={({ isActive }) => navLinkClass(isActive, lockedWithoutSite)}
                    aria-disabled={lockedWithoutSite || undefined}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            <div className="shrink-0 lg:mt-auto lg:border-t lg:border-border lg:pt-3 lg:pr-4">
              <NavLink
                to={DASHBOARD_CONTACT_ADMIN.to}
                end={DASHBOARD_CONTACT_ADMIN.end}
                onClick={(e) => {
                  if (contactLocked) {
                    e.preventDefault();
                    return;
                  }
                  if (!confirmLeaveIfDirty()) e.preventDefault();
                }}
                className={({ isActive }) => navLinkClass(isActive, contactLocked, 'contact')}
                aria-disabled={contactLocked || undefined}
              >
                <Mail className="h-4 w-4 shrink-0" aria-hidden />
                {DASHBOARD_CONTACT_ADMIN.label}
              </NavLink>
            </div>
          </aside>

          <div className="min-w-0 flex-1 animate-in fade-in duration-200">
            <Outlet />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function DashboardLayout() {
  return (
    <DashboardSiteProvider>
      <DashboardShell />
    </DashboardSiteProvider>
  );
}
